import type { AgendaItem } from "@repo/types";
import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { Button } from "@repo/ui/components/Base/Button";
import { TagSelection } from "../tags";
import { AgendaItemSortableList } from "../dnd";
import { useContext, useRef } from "react";
import { SearchableAgendaItemTable } from "../common/SearchableTables";
import { useTrackedNewObjects } from "@/hooks/trackCreatedObjects";
import { Label } from "@repo/ui/components/Base/Label";
import { AgendaModuleDefaultsContext } from "./AgendaModuleDefaults";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { useAgendaItemList } from "@/hooks/queryHooks/agendaItemHooks";
import { useActiveTeamId } from "@/hooks/useActiveTeam";
import {
  agendaModuleRepo,
  type AgendaModuleDto,
} from "@/repositories/agendaModuleRepo";

export interface AgendaModuleFormProps {
  agendaModuleFormData?: AgendaModuleDto;
  partialAgendaModuleFormData?: Partial<AgendaModuleDto>;
  tenantId: string;
  handleCancel: () => void;
  handleComplete: () => void;
  onSubmit?: (agendaModule: AgendaModuleDto) => void;
  addAnother?: () => void;
  isTemplate: boolean;
  viewOnly?: boolean;
  handleGoBack?: () => void;
  cancelButtonLabel?: string;
  withinDialog?: boolean;
}
export const AgendaModuleForm = ({
  agendaModuleFormData,
  partialAgendaModuleFormData,
  tenantId,
  handleCancel,
  handleComplete,
  onSubmit,
  addAnother,
  isTemplate,
  viewOnly,
  handleGoBack,
  cancelButtonLabel,
  withinDialog,
}: AgendaModuleFormProps) => {
  const activeTeamId = useActiveTeamId();
  const { data: agendaItems } = useAgendaItemList(tenantId, activeTeamId || "");

  const validationSchema = yup.object({
    name: yup.string().required("Item name is required"),
  });

  const trackNew = useTrackedNewObjects();

  const formAction = useRef<"add" | "addAnother">("add");

  const defaults = useContext(AgendaModuleDefaultsContext);

  if (!activeTeamId) {
    // bail if team is org or hasn't loaded yet
    if (activeTeamId === null) return <p>Loading...</p>;
    return null;
  }

  return (
    <div>
      <Formik
        initialValues={{
          tags: [],
          ...(agendaModuleFormData ??
            agendaModuleRepo.init({
              ...defaults,
              teamId: activeTeamId,
              template: isTemplate,
              ...partialAgendaModuleFormData,
            })),
        }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          if (!tenantId) {
            console.log("No tenant ID", tenantId);
            return;
          }
          console.log(values);

          const updatedAgendaModule = agendaModuleFormData
            ? await agendaModuleRepo.update(tenantId, values.id, { ...values })
            : await agendaModuleRepo.create(tenantId, { ...values });

          if (!agendaModuleFormData) {
            trackNew("agendaModule", updatedAgendaModule); // track created agendaModules
          }

          if (updatedAgendaModule) onSubmit?.(updatedAgendaModule);
          handleComplete();
          if (formAction.current === "addAnother" && addAnother) {
            setTimeout(addAnother, 300); // wait for close animation to complete so component remounts
          }
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          errors,
          touched,
          isSubmitting,
          submitForm,
        }) => {
          const onAddAgendaItem = (item: AgendaItem) => {
            setFieldValue(
              "agendaItemIds",
              values.agendaItemIds
                ? [...values.agendaItemIds, item.id]
                : [item.id]
            );
            setFieldValue(
              "agendaItems",
              values.agendaItems ? [...values.agendaItems, item] : [item]
            );
          };

          const footer = (
            <>
              <Button
                variant="destructive"
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  viewOnly ? handleGoBack && handleGoBack() : handleCancel();
                }}
              >
                {cancelButtonLabel
                  ? cancelButtonLabel
                  : viewOnly
                    ? "Go Back"
                    : "Cancel"}
              </Button>
              <div className="flex-1" />
              {!viewOnly && (
                <>
                  {addAnother ? (
                    <>
                      <Button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => {
                          formAction.current = "addAnother";
                          submitForm();
                        }}
                      >
                        Save and Add Another
                      </Button>
                      <div className="w-3" />
                    </>
                  ) : null}
                  <Button type="submit" disabled={isSubmitting}>
                    Save
                  </Button>
                </>
              )}
            </>
          );

          const form = (
            <div className="flex flex-col gap-y-4">
              <Input
                disabled={viewOnly || isSubmitting}
                label="*Agenda Section Name"
                id="name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.name && touched.name}
                errorMessage={errors.name}
              />
              <TextArea
                disabled={viewOnly || isSubmitting}
                label="Additional Text"
                id="description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.description && touched.description}
                errorMessage={errors.description}
              />

              <TagSelection
                disabled={viewOnly || isSubmitting}
                label="Tags"
                value={values.tags}
                onChange={(newTags) => setFieldValue("tags", newTags)}
                enableAutocomplete={true}
                useStandardizedTags={true}
              />

              <Label label="Agenda Items" />
              {values.agendaItems && agendaItems && (
                <AgendaItemSortableList
                  disabled={viewOnly || isSubmitting}
                  items={
                    values.agendaItemIds
                      ? values.agendaItemIds.map((id) => {
                          return agendaItems.find((item) => item.id === id)!;
                        })
                      : []
                  }
                  onChange={(items) => {
                    setFieldValue(
                      "agendaItemIds",
                      items.map((item) => item.id)
                    );
                    setFieldValue("agendaItems", items);
                  }}
                  editableLabel="Edit Agenda Item"
                  deleteLabel="Remove Agenda Item"
                />
              )}

              {agendaItems && !viewOnly && (
                <SearchableAgendaItemTable
                  disabled={isSubmitting}
                  onSubmit={onAddAgendaItem}
                  onAdd={onAddAgendaItem}
                  data={agendaItems.filter(
                    (doc) => !values.agendaItemIds?.includes(doc.id)
                  )}
                />
              )}

              {!withinDialog && (
                <div className="flex flex-row justify-between">{footer}</div>
              )}
            </div>
          );

          return (
            <form onSubmit={handleSubmit}>
              {withinDialog ? (
                <>
                  <DialogBody>{form}</DialogBody>
                  <DialogFooter className="justify-between">
                    {footer}
                  </DialogFooter>
                </>
              ) : (
                form
              )}
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
